import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Upload, Clock, SlidersHorizontal, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useGestaoDocumentos, type TipoDocumento } from '@/contexts';
import { GestaoDocumentosProvider, TabLabel, Button } from '../GestaoDocumentosProvider';
import { GrupoDocumentosSection } from './GrupoDocumentosSection';
import { UploadXmlDialog } from './UploadXmlDialog';
import { HistoricoProcessamentosDialog } from './HistoricoProcessamentosDialog';
import { ExportarModalSN } from './ExportarModalSN';

function GestaoDocumentosContent() {
  const {
    cnpjSelecionado,
    tabAtiva,
    setTabAtiva,
    filtroPendente,
    setFiltroPendente,
    aplicarFiltros,
    resetarFiltros,
    filtroBase,
    filtrosVisiveis,
    setFiltrosVisiveis,
    uploadAberto,
    abrirUpload,
    fecharUpload,
    historicoAberto,
    abrirHistorico,
    fecharHistorico,
    grupos,
    totalEntradas,
    totalSaidas,
    isLoadingEntradas,
    isLoadingSaidas,
    isError,
  } = useGestaoDocumentos();

  const [exportarAberto, setExportarAberto] = useState(false);

  return (
    <div className="min-h-screen bg-background pt-6">
      <main className="px-6 pt-3 pb-6 space-y-6">
        {/* Cabeçalho */}
        <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted shrink-0">
            <FileText className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="text-base font-semibold text-foreground">
            Gestão de Documentos Fiscais
          </span>
          <Button
            variant="outline"
            size="icon"
            className={cn('ml-auto', filtrosVisiveis && 'border-accent text-accent')}
            onClick={() => setFiltrosVisiveis(!filtrosVisiveis)}
            aria-expanded={filtrosVisiveis}
            title="Filtros"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            title="Exportar"
            onClick={() => setExportarAberto(true)}
          >
            <Upload className="h-4 w-4" />
          </Button>
        </div>

        {/* Drawer de Filtros */}
        <Sheet open={filtrosVisiveis} onOpenChange={setFiltrosVisiveis}>
          <SheetContent side="right" className="w-80 flex flex-col gap-0 p-0">
            <SheetHeader className="px-6 py-4 border-b">
              <SheetTitle className="text-base">Filtros</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Modelo
                </label>
                <Select
                  value={filtroPendente.modelo || '_todos'}
                  onValueChange={v =>
                    setFiltroPendente(f => ({ ...f, modelo: v === '_todos' ? '' : v }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todos os modelos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_todos">Todos</SelectItem>
                    <SelectItem value="55">NF-e (Modelo 55)</SelectItem>
                    <SelectItem value="65">NFC-e (Modelo 65)</SelectItem>
                    <SelectItem value="57">CT-e (Modelo 57)</SelectItem>
                    <SelectItem value="66">NF3-e (Modelo 66)</SelectItem>
                    <SelectItem value="62">NFCom (Modelo 62)</SelectItem>
                    <SelectItem value="76">NFGas (Modelo 76)</SelectItem>
                    <SelectItem value="13">NFS-e (Modelo 13)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Status
                </label>
                <Select
                  value={filtroPendente.status || '_todos'}
                  onValueChange={v =>
                    setFiltroPendente(f => ({ ...f, status: v === '_todos' ? '' : v }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todos os status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_todos">Todos</SelectItem>
                    <SelectItem value="4">Homologado</SelectItem>
                    <SelectItem value="5">Denegado</SelectItem>
                    <SelectItem value="7">Cancelado</SelectItem>
                    <SelectItem value="8">Divergência</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <SheetFooter className="flex flex-row gap-2 px-6 py-4 border-t">
              <Button variant="outline" className="flex-1" onClick={resetarFiltros}>
                Resetar
              </Button>
              <Button className="flex-1" onClick={aplicarFiltros}>
                Aplicar Filtros
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Tabs */}
        <Tabs value={tabAtiva} onValueChange={v => setTabAtiva(v as TipoDocumento)}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="entradas">
                <TabLabel label="Entradas" total={totalEntradas} isLoading={isLoadingEntradas} />
              </TabsTrigger>
              <TabsTrigger
                value="saidas"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
              >
                <TabLabel label="Saídas" total={totalSaidas} isLoading={isLoadingSaidas} />
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 bg-card"
                        disabled={!cnpjSelecionado}
                        onClick={abrirHistorico}
                      >
                        <Clock className="h-4 w-4" />
                        Histórico
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {cnpjSelecionado
                      ? 'Histórico de Processamentos'
                      : 'Selecione uma empresa para ver o histórico'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="default"
                size="sm"
                className="gap-2 bg-[#aed561] text-white hover:bg-[#8ab83a]"
                disabled={!cnpjSelecionado}
                onClick={abrirUpload}
              >
                <Upload className="h-4 w-4" />
                Upload XML
              </Button>
            </div>
          </div>

          {(['entradas', 'saidas'] as TipoDocumento[]).map(tab => {
            const isTabLoading = tab === 'entradas' ? isLoadingEntradas : isLoadingSaidas;
            return (
              <TabsContent key={tab} value={tab} className="mt-0 space-y-3">
                {!cnpjSelecionado ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-muted-foreground">
                    <FileText className="h-8 w-8 opacity-40" />
                    <p className="text-sm">Selecione uma empresa para visualizar os documentos.</p>
                  </div>
                ) : isTabLoading ? (
                  <div className="flex items-center justify-center gap-2 rounded-lg border py-16 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <p className="text-sm">Carregando documentos...</p>
                  </div>
                ) : isError ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 py-16 text-destructive">
                    <AlertCircle className="h-8 w-8 opacity-70" />
                    <p className="text-sm font-medium">Erro ao carregar documentos.</p>
                    <p className="text-xs text-muted-foreground">
                      Verifique sua conexão e tente novamente.
                    </p>
                  </div>
                ) : grupos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-muted-foreground">
                    <FileText className="h-8 w-8 opacity-40" />
                    <p className="text-sm">
                      {tab === 'entradas'
                        ? 'Nenhum documento de entrada encontrado para o período selecionado.'
                        : 'Nenhum documento de saída encontrado para o período selecionado.'}
                    </p>
                  </div>
                ) : (
                  grupos.map(grupo => (
                    <GrupoDocumentosSection
                      key={grupo.codigoModelo}
                      grupo={grupo}
                      tipo={tab}
                      filtroBase={filtroBase}
                    />
                  ))
                )}
              </TabsContent>
            );
          })}
        </Tabs>

        <UploadXmlDialog
          open={uploadAberto}
          onOpenChange={open => (open ? abrirUpload() : fecharUpload())}
          cnpj={cnpjSelecionado ?? ''}
        />

        <HistoricoProcessamentosDialog
          open={historicoAberto}
          onOpenChange={open => (open ? abrirHistorico() : fecharHistorico())}
          cnpj={cnpjSelecionado ?? ''}
        />

        <ExportarModalSN
          open={exportarAberto}
          onOpenChange={setExportarAberto}
          tipoDocumento={tabAtiva === 'entradas' ? '0' : '1'}
          cnpjContribuinte={cnpjSelecionado ?? ''}
          dataInicio={filtroBase.data_emissao_de}
          dataFim={filtroBase.data_emissao_ate}
        />
      </main>
    </div>
  );
}

export function GestaoDocumentosPage() {
  return (
    <GestaoDocumentosProvider>
      <GestaoDocumentosContent />
    </GestaoDocumentosProvider>
  );
}
